import React, { useState, useRef } from 'react';
import propTypes from 'prop-types';
import { connect } from 'react-redux';

import { Card, CardContent, Column, Columns } from 'src/ui/mui-ui-kit';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { uniqueId } from 'lodash';
import {
  resetFileInput,
  reduceServerErrors,
  UiToastAdapter,
  stripHtml,
} from 'src/ui';
import { getUser } from 'src/user';
import { addAccountDocument, getAccount } from 'src/account';
import { toast } from 'react-toastify';
import dayjs from 'dayjs-ext';
import { DocumentCardItem } from './DocumentCardItem';
import { AddDocumentModal } from './AddDocumentModal';
import { DEFAULT_DATE_FORMAT } from '../../ui/const';
/*eslint-disable */
const DocumentCard = ({
  documentType,
  onDeleteDocument,
  user,
  account,
  addAccountDocument,
  onOpenEditDocumentType,
  isBankAdmin,
}) => {
  const inputRef = useRef(null);
  const [uploadingDocuments, setUploadingDocuments] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { frequencyType, isCustom, needsApproval } = documentType;
  const handleSelectFile = () => {
    const { files } = inputRef.current;

    if (files.length) {
      const [file] = files;
      uploadDocument(file);
    }

    resetFileInput(inputRef.current);
  };

  const uploadDocument = async (file, expirationDate, uploadDate) => {
    const title = file.name.substring(0, file.name.lastIndexOf('.'));
    const uploadingTemp = {
      title,
      id: uniqueId('document'),
    };

    setUploadingDocuments([...uploadingDocuments, uploadingTemp]);

    await addAccountDocument({
      title,
      accountId: user.accountId,
      type: documentType.id,
      file,
      expirationDate: dayjs(expirationDate).format(DEFAULT_DATE_FORMAT) || null,
      uploadDate: uploadDate
        ? uploadDate
        : dayjs(new Date()).format(DEFAULT_DATE_FORMAT),
    });

    if (account.isError()) {
      return reduceServerErrors(account);
    }

    toast(UiToastAdapter('Document added successfully!', 'success'));

    return removeFromPending(uploadingTemp.id);
  };

  const removeFromPending = id => {
    const filteredUploads = uploadingDocuments.filter(doc => doc.id !== id);
    setUploadingDocuments(filteredUploads);
  };

  return (
    <Card className="document-card">
      <CardContent>
        <Columns isVCentered isGapless>
          <Column>
            {isCustom && isBankAdmin ? (
              <a
                onClick={() => onOpenEditDocumentType(documentType)}
                className="title is-5 has-text-primary"
              >
                {documentType.name}
              </a>
            ) : (
              <p className="title is-5 has-text-primary">{documentType.name}</p>
            )}
          </Column>
          <Column isSize="narrow">
            <label
              htmlFor={`upload-${documentType.id}`}
              className="button is-text is-rounded upload-button"
            >
              <span
                onClick={
                  frequencyType === 'Expiration' || !needsApproval
                    ? () => setIsModalVisible(true)
                    : null
                }
                className="icon is-medium has-text-info"
              >
                <FontAwesomeIcon icon={['fal', 'cloud-upload']} size="lg" />
              </span>
              {frequencyType !== 'Expiration' && needsApproval && (
                <input
                  type="file"
                  id={`upload-${documentType.id}`}
                  onChange={handleSelectFile}
                  ref={inputRef}
                />
              )}
            </label>
          </Column>
        </Columns>
        {documentType.description && (
          <div className="has-text-grey-medium-dark is-size-7">
            {documentType.nextUploadDate
              ? `Next Upload: ${documentType.nextUploadDate} `
              : null}
            <br />
            {stripHtml(documentType.description).substr(0, 220)}
            {documentType.description.length > 220 && '...'}
          </div>
        )}
      </CardContent>
      {documentType.documents.map(document => (
        <DocumentCardItem
          document={document}
          key={document.id}
          onDelete={onDeleteDocument}
          showLink={user.isPrimaryMerchantAdmin || user.isBankAdmin}
          needsApproval={needsApproval}
        />
      ))}
      {uploadingDocuments.map(document => (
        <DocumentCardItem document={document} key={document.id} loading />
      ))}
      {isModalVisible && (
        <AddDocumentModal
          isActive={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          onSubmit={uploadDocument}
          needsApproval={needsApproval}
          expirationOnUpload={frequencyType === 'Expiration'}
          isCustom={isCustom}
        />
      )}
    </Card>
  );
};

DocumentCard.propTypes = {
  documentType: propTypes.shape({
    id: propTypes.number.isRequired,
    name: propTypes.string.isRequired,
    description: propTypes.string,
    documents: propTypes.arrayOf(propTypes.object).isRequired,
  }).isRequired,
  user: propTypes.object.isRequired,
  account: propTypes.object.isRequired,
  addAccountDocument: propTypes.func.isRequired,
  onDeleteDocument: propTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    user: getUser(state),
    account: getAccount(state),
  };
}

const connectedDocumentCard = connect(mapStateToProps, {
  addAccountDocument,
})(DocumentCard);

export { connectedDocumentCard as DocumentCard };
